import React, { FC, useMemo } from 'react'
import { Card, Box, Typography, useTheme } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import DOMPurify from 'dompurify'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehype from 'rehype-raw'

import { format } from '@e3dc-react/shell/Libs/date'
import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { DialogActions } from '@e3dc-react/shell/Elements/Dialog/Dialog'
import { getReactMarkdownComponents } from 'src/Libs/notifications/config'
import { MessageData } from '../../../models'
import { getHardwareTestMessage } from '../../../utils'

export interface TestRunDetailsModalProps {
  onClose?: () => void
  messageData: MessageData
  testId: string
  testDate: string
}

const useStyles = makeStyles()(theme => ({
  card: {
    boxShadow: 'none',
    padding: theme.spacing(2),
    background: theme.palette.shades.shade04,
    marginTop: theme.spacing(4),
    minWidth: '1000px',
  },
  cardTitleContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingBottom: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.shades.shade03}`,
    marginBottom: theme.spacing(2),
  },
  messageText: {
    whiteSpace: 'pre-wrap',
    fontSize: '14px',
    display: 'flex',
    flexDirection: 'column',
  },
}))

/**
 * Displays full test run details
 */
const TestRunDetailsModal: FC<TestRunDetailsModalProps> = ({ onClose, messageData, testId, testDate }) => {
  const theme = useTheme()
  const { enqueueSuccessStack } = useSnackbar()
  const { classes } = useStyles()
  const { t } = useCustomTranslation()
  const messageDataText = getHardwareTestMessage(messageData, t)
  const reactMarkdownComponents = useMemo(() => getReactMarkdownComponents(theme), [theme])

  const copyToClipboard = (): void => {
    window.navigator.clipboard.writeText(messageDataText)
    enqueueSuccessStack(t('system-details.hardware-test.test-run-details-dialog.copy-successful-message'))
  }

  const closeAction: DialogActions = {
    primary: {
      name: t('system-details.hardware-test.test-run-details-dialog.copy-button'),
      onClick: copyToClipboard,
    },
    secondary: [
      {
        name: t('common.close'),
        onClick: () => onClose?.(),
      },
    ],
  }

  return (
    <Dialog title={t('system-details.hardware-test.test-run-details-dialog.title', { testId })} actions={closeAction} maxWidth="md" disableClose>
      <Card variant="outlined" className={classes.card}>
        <Box className={classes.cardTitleContainer}>
          <Typography variant="body2">{t('system-details.hardware-test.test-run-details-dialog.card-title', { testId })}</Typography>
          <Typography variant="body2">{format(new Date(testDate), t('common.format.date-fns-date'))}</Typography>
        </Box>
        <ReactMarkdown components={reactMarkdownComponents} remarkPlugins={[remarkGfm]} rehypePlugins={[rehype]} className={classes.messageText}>
          {DOMPurify.sanitize(messageDataText)}
        </ReactMarkdown>
      </Card>
    </Dialog>
  )
}

export default TestRunDetailsModal
